/**
 * Future Closure Data Warnings Component
 * P4: Enhanced error handling and validation warnings
 */

import React from 'react';
import { XCircle, AlertTriangle, Info } from 'lucide-react';

interface DataWarningsProps {
  reportData?: any;
  onDismiss?: () => void;
}

interface Warning {
  type: 'error' | 'warning' | 'info';
  title: string;
  message: string;
  action?: string;
}

export default function FutureClosureDataWarnings({ reportData, onDismiss }: DataWarningsProps) {
  const getWarnings = (): Warning[] => {
    const warnings: Warning[] = [];

    if (!reportData) {
      warnings.push({
        type: 'error',
        title: 'No Report Data',
        message: 'Unable to load report data. Please refresh the page or try again.',
        action: 'Refresh Page'
      });
      return warnings;
    }

    const financialData = reportData.financial_data;

    // Check if financial data exists
    if (!financialData || Object.keys(financialData).length === 0) {
      warnings.push({
        type: 'error',
        title: 'No Financial Data',
        message: 'This report does not have monthly P&L data uploaded. Future closure calculations require historical financial data.',
        action: 'Upload P&L Data'
      });
      return warnings;
    }

    // Count months of data
    let totalMonths = 0;
    let yearsWithData: string[] = [];
    let hasRevenueData = false;
    let hasExpenseData = false;

    Object.keys(financialData).forEach(key => {
      if (key.startsWith('data_')) {
        const year = key.replace('data_', '');
        const yearData = financialData[key];
        if (Array.isArray(yearData) && yearData.length > 0) {
          yearsWithData.push(year);
          totalMonths += yearData.length;

          // Check for revenue and expense data
          yearData.forEach((month: any) => {
            const revenue = parseFloat(month.revenue) || parseFloat(month.Revenue) || 0;
            if (revenue > 0) hasRevenueData = true;
            const expenses = 
              (parseFloat(month.officer_salary) || parseFloat(month.Officer_Salary) || 0) +
              (parseFloat(month.employee_wages) || parseFloat(month.Employee_Wages) || 0) +
              (parseFloat(month.rent) || parseFloat(month.Rent) || 0);
            if (expenses > 0) hasExpenseData = true;
          });
        }
      }
    });

    // Check minimum data requirements
    if (totalMonths < 12) {
      warnings.push({
        type: 'error',
        title: 'Insufficient Historical Data',
        message: `Only ${totalMonths} months of data available. Future closure calculations require at least 12 months of historical data for accurate projections.`,
        action: 'Upload More Data'
      });
    } else if (totalMonths < 24) {
      warnings.push({
        type: 'warning',
        title: 'Limited Historical Data',
        message: `Only ${totalMonths} months of data available. For more accurate projections and seasonality detection, 24+ months of data is recommended.`
      });
    }

    // Check for revenue data
    if (!hasRevenueData) {
      warnings.push({
        type: 'error',
        title: 'No Revenue Data Found',
        message: 'The uploaded financial data does not contain revenue information. Revenue data is required for loss projections.'
      });
    }

    // Check for expense data
    if (!hasExpenseData) {
      warnings.push({
        type: 'warning',
        title: 'Limited Expense Data',
        message: 'Some expense categories appear to be missing or zero. This may affect the accuracy of continued expense calculations.'
      });
    }

    // Check for data gaps
    if (yearsWithData.length > 1) {
      const years = yearsWithData.map(y => parseInt(y)).sort();
      for (let i = 1; i < years.length; i++) {
        if (years[i] - years[i-1] > 1) {
          warnings.push({
            type: 'warning',
            title: 'Gap in Historical Data',
            message: `There appears to be a gap in your data between ${years[i-1]} and ${years[i]}. This may affect seasonality calculations.`
          });
          break;
        }
      }
    }

    // Check data recency
    if (yearsWithData.length > 0) {
      const latestYear = Math.max(...yearsWithData.map(y => parseInt(y)));
      const currentYear = new Date().getFullYear();
      if (currentYear - latestYear > 2) {
        warnings.push({
          type: 'warning',
          title: 'Older Historical Data',
          message: `The most recent data is from ${latestYear}. Consider updating with more recent financial data for accurate projections.`
        });
      }
    }

    // Add info about what data was found
    if (warnings.length === 0 || warnings.every(w => w.type !== 'error')) {
      warnings.push({
        type: 'info',
        title: 'Data Quality',
        message: `Found ${totalMonths} months of data across ${yearsWithData.length} year(s) (${yearsWithData.join(', ')}). Ready for calculation.`
      });
    }

    return warnings;
  };

  const warnings = getWarnings();
  const hasErrors = warnings.some(w => w.type === 'error');
  const hasWarnings = warnings.some(w => w.type === 'warning');

  // Don't show if only info messages
  if (!hasErrors && !hasWarnings) {
    return null;
  }

  const getIconAndColors = (type: string): { bg: string; border: string; icon: React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>; titleColor: string; textColor: string } => {
    switch (type) {
      case 'error':
        return {
          bg: 'var(--color-needs-human-tint)',
          border: 'var(--color-needs-human-tint-bd)',
          icon: XCircle,
          titleColor: 'var(--color-needs-human-text)',
          textColor: 'var(--color-needs-human-text)'
        };
      case 'warning':
        return {
          bg: 'var(--color-needs-human-tint)',
          border: 'var(--color-needs-human-tint-bd)',
          icon: AlertTriangle,
          titleColor: 'var(--color-brand)',
          textColor: 'var(--color-brand)'
        };
      default:
        return {
          bg: 'var(--color-needs-human-tint)',
          border: 'var(--color-needs-human-tint-bd)',
          icon: Info,
          titleColor: 'var(--color-needs-human-text)',
          textColor: 'var(--color-needs-human-text)'
        };
    }
  };

  return (
    <div className="space-y-3">
      {warnings.filter(w => w.type !== 'info').map((warning, idx) => {
        const styles = getIconAndColors(warning.type);
        return (
          <div
            key={idx}
            className="border rounded-lg p-4"
            style={{ backgroundColor: styles.bg, borderColor: styles.border }}
          >
            <div className="flex items-start space-x-3">
              <styles.icon size={20} className="flex-shrink-0" style={{ color: styles.titleColor }} />
              <div className="flex-1">
                <h4 className="font-medium" style={{ color: styles.titleColor }}>{warning.title}</h4>
                <p className="text-sm mt-1" style={{ color: styles.textColor }}>{warning.message}</p>
                {warning.action && (
                  <button className="text-sm font-medium mt-2 underline hover:no-underline">
                    {warning.action}
                  </button>
                )}
              </div>
              {onDismiss && (
                <button
                  onClick={onDismiss}
                  style={{ color: 'var(--color-text-dim)' }}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
